import {
  PAIRE_CELL_COUNT,
  PAIRE_SIZE,
  PAIRE_SYMBOLS,
  type PaireConstraint,
  type PaireSymbol,
  type PaireValue,
} from './types';

export function paireUnits(): number[][] {
  const units: number[][] = [];
  for (let row = 0; row < PAIRE_SIZE; row += 1) {
    units.push(Array.from({ length: PAIRE_SIZE }, (_, column) => row * PAIRE_SIZE + column));
  }
  for (let column = 0; column < PAIRE_SIZE; column += 1) {
    units.push(Array.from({ length: PAIRE_SIZE }, (_, row) => row * PAIRE_SIZE + column));
  }
  return units;
}

export const PAIRE_UNITS = paireUnits();

export function findPaireConflicts(
  board: readonly PaireValue[],
  constraints: readonly PaireConstraint[],
): Set<number> {
  const conflicts = new Set<number>();
  for (const unit of PAIRE_UNITS) {
    for (const symbol of PAIRE_SYMBOLS) {
      const matches = unit.filter((index) => board[index] === symbol);
      if (matches.length > 3) matches.forEach((index) => conflicts.add(index));
    }
    for (let start = 0; start <= PAIRE_SIZE - 3; start += 1) {
      const triple = unit.slice(start, start + 3);
      const value = board[triple[0]];
      if (value !== 0 && triple.every((index) => board[index] === value)) {
        triple.forEach((index) => conflicts.add(index));
      }
    }
  }
  for (const constraint of constraints) {
    const first = board[constraint.a];
    const second = board[constraint.b];
    if (first === 0 || second === 0) continue;
    const violated = constraint.kind === 'eq' ? first !== second : first === second;
    if (violated) {
      conflicts.add(constraint.a);
      conflicts.add(constraint.b);
    }
  }
  return conflicts;
}

export function isPaireBoardValid(
  board: readonly PaireValue[],
  constraints: readonly PaireConstraint[],
): boolean {
  return board.length === PAIRE_CELL_COUNT && findPaireConflicts(board, constraints).size === 0;
}

export function isPaireComplete(
  board: readonly PaireValue[],
  constraints: readonly PaireConstraint[],
): boolean {
  return !board.includes(0) && isPaireBoardValid(board, constraints);
}

export function countPaireSolutions(
  puzzle: readonly PaireValue[],
  constraints: readonly PaireConstraint[],
  limit = 2,
): number {
  if (puzzle.length !== PAIRE_CELL_COUNT || !isPaireBoardValid(puzzle, constraints)) return 0;
  const board = [...puzzle];
  let count = 0;

  function search() {
    if (count >= limit) return;
    let target = -1;
    let options: PaireSymbol[] = [];
    for (let index = 0; index < PAIRE_CELL_COUNT; index += 1) {
      if (board[index] !== 0) continue;
      const candidates = PAIRE_SYMBOLS.filter((symbol) =>
        canPlacePaire(board, constraints, index, symbol),
      );
      if (candidates.length === 0) return;
      if (target === -1 || candidates.length < options.length) {
        target = index;
        options = candidates;
        if (options.length === 1) break;
      }
    }
    if (target === -1) {
      count += 1;
      return;
    }
    for (const symbol of options) {
      board[target] = symbol;
      search();
      board[target] = 0;
      if (count >= limit) return;
    }
  }

  search();
  return count;
}

export function canPlacePaire(
  board: readonly PaireValue[],
  constraints: readonly PaireConstraint[],
  index: number,
  symbol: PaireSymbol,
): boolean {
  const next = [...board];
  next[index] = symbol;
  return findPaireConflicts(next, constraints).size === 0;
}

export function allPaireConstraints(solution: readonly PaireSymbol[]): PaireConstraint[] {
  const constraints: PaireConstraint[] = [];
  for (let row = 0; row < PAIRE_SIZE; row += 1) {
    for (let column = 0; column < PAIRE_SIZE; column += 1) {
      const index = row * PAIRE_SIZE + column;
      if (column < PAIRE_SIZE - 1) constraints.push(makeConstraint(index, index + 1, solution));
      if (row < PAIRE_SIZE - 1)
        constraints.push(makeConstraint(index, index + PAIRE_SIZE, solution));
    }
  }
  return constraints;
}

function makeConstraint(a: number, b: number, solution: readonly PaireSymbol[]): PaireConstraint {
  return { a, b, kind: solution[a] === solution[b] ? 'eq' : 'neq' };
}
